// Process controllers (e.g. Selec PID500-U) — shares the generic Modbus item model and
// value engine with VFDs (utils/vfd.ts); only the roles and API endpoints differ.

import { API_BASE } from './api-base';
import type { VfdItem, VfdPresetBrand } from './vfd';

export type ControllerRole = '' | 'pv' | 'sv' | 'output' | 'output2' | 'alarm1' | 'alarm2' | 'mode' | 'run' | 'fault';

export const CONTROLLER_ROLE_OPTIONS: { value: ControllerRole; label: string }[] = [
  { value: '', label: 'Other parameter' },
  { value: 'pv', label: 'Process value (PV)' },
  { value: 'sv', label: 'Set point (SV)' },
  { value: 'output', label: 'Output % (heat)' },
  { value: 'output2', label: 'Output 2 % (cool)' },
  { value: 'alarm1', label: 'Alarm 1' },
  { value: 'alarm2', label: 'Alarm 2' },
  { value: 'mode', label: 'Auto / manual' },
  { value: 'run', label: 'Run / standby' },
  { value: 'fault', label: 'Sensor fault' },
];

export interface ControllerDisplay {
  pvMin?: number;
  pvMax?: number;
  bandAbove?: number;
  bandBelow?: number;
}

export interface ProcessController {
  id: string;
  clientId: string;
  name: string;
  description?: string;
  location?: string;
  brand?: string;
  modelId?: string;
  modelName?: string;
  connectionType: 'rtu' | 'tcp';
  serialPort?: string;
  baudRate?: number;
  dataBits?: number;
  stopBits?: number;
  parity?: 'none' | 'even' | 'odd';
  slaveId?: number;
  ipAddress?: string;
  port?: number;
  pollIntervalMs?: number;
  timeoutMs?: number;
  display: ControllerDisplay;
  items: VfdItem[];
  isActive?: boolean;
  bridgeId?: string | null;
}

export interface ControllerPresetModel {
  id: string;
  name: string;
  status: string;
  note?: string;
  source?: string;
  defaults?: { baudRate?: number; parity?: string; dataBits?: number; stopBits?: number; slaveId?: number };
  items: VfdItem[];
}
export type ControllerPresetBrand = Omit<VfdPresetBrand, 'models'> & { models: ControllerPresetModel[] };

export interface ControllerTestResult {
  ok: boolean;
  error?: string | null;
  link?: string;
  bridgeId?: string;
  connectMs?: number | null;
  elapsedMs?: number | null;
  roundTripMs?: number;
  items: { name: string; role: string; func: 3 | 4; addr: number | null; ok: boolean; raw?: number; value?: number; error?: string }[];
  explore?: { start: number; count: number; input: { addr: number; raw?: number; error?: string }[]; holding: { addr: number; raw?: number; error?: string }[] } | null;
}

const authHeaders = (token: string) => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' });

async function request<T>(url: string, token: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${url}`, { ...init, headers: { ...authHeaders(token), ...(init.headers || {}) } });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `Request failed (HTTP ${res.status})`);
  return body as T;
}

export const controllerApi = {
  listForClient: (token: string, clientId: string) =>
    request<{ controllers: ProcessController[] }>(`/api/clients/${clientId}/controllers`, token).then((r) => r.controllers || []),
  adminList: (token: string, clientId: string) =>
    request<{ controllers: ProcessController[] }>(`/api/admin/clients/${clientId}/controllers`, token).then((r) => r.controllers || []),
  presets: (token: string) =>
    request<{ brands: ControllerPresetBrand[] }>('/api/admin/controller-presets', token).then((r) => r.brands || []),
  create: (token: string, clientId: string, c: Partial<ProcessController>) =>
    request<{ controller: ProcessController }>(`/api/admin/clients/${clientId}/controllers`, token, { method: 'POST', body: JSON.stringify(c) }).then((r) => r.controller),
  update: (token: string, clientId: string, id: string, c: Partial<ProcessController>) =>
    request<{ controller: ProcessController }>(`/api/admin/clients/${clientId}/controllers/${id}`, token, { method: 'PUT', body: JSON.stringify(c) }).then((r) => r.controller),
  remove: (token: string, clientId: string, id: string) =>
    request<{ success: boolean }>(`/api/admin/clients/${clientId}/controllers/${id}`, token, { method: 'DELETE' }),
  test: (token: string, clientId: string, body: Record<string, any>) =>
    request<ControllerTestResult>(`/api/admin/clients/${clientId}/controllers/test`, token, { method: 'POST', body: JSON.stringify(body) }),
};
